import React, { useState, useEffect, useCallback } from "react";
import { Plus, Search, Eye, Edit2, SlidersHorizontal } from "lucide-react";
import FormControl from "../common/FormControl";
import Button from "../common/Button";
import Badge from "../common/Badge";
import Pagination from "../common/Pagination";
import Toast, { ToastMessage } from "../common/Toast";
import Skeleton from "../common/Skeleton";
import { InventoryCluster, InventoryStatistics } from "../../types/inventory";
import realInventoryService from "../../services/inventoryService";
import { statusVariant, severityBadgeCss } from "../../utils/statusVariant";
import InventoryCategoriesModal from "./modals/InventoryCategoriesModal";
import InventoryTypesModal from "./modals/InventoryTypesModal";
import InventoryStatusesModal from "./modals/InventoryStatusesModal";
import InventoryItemsModal from "./modals/InventoryItemsModal";
import InventoryClustersModal from "./modals/InventoryClustersModal";
import ClusterDetailsModal from "./modals/ClusterDetailsModal";

const SDLCManagement: React.FC<{ compact?: boolean }> = ({
  compact = false,
}) => {
  const [clusters, setClusters] = useState<InventoryCluster[]>([]);
  const [statistics, setStatistics] = useState<InventoryStatistics | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [categoriesModalOpen, setCategoriesModalOpen] = useState(false);
  const [typesModalOpen, setTypesModalOpen] = useState(false);
  const [statusesModalOpen, setStatusesModalOpen] = useState(false);
  const [inventoryModalOpen, setInventoryModalOpen] = useState(false);
  const [clustersModalOpen, setClustersModalOpen] = useState(false);
  const [clusterDetailsModalOpen, setClusterDetailsModalOpen] = useState(false);
  const [selectedCluster, setSelectedCluster] =
    useState<InventoryCluster | null>(null);
  const [showChips, setShowChips] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, typeFilter, pageSize]);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const [clustersData, statisticsData] = await Promise.all([
        realInventoryService.getClusters(),
        realInventoryService.getStatistics(),
      ]);
      setClusters(clustersData);
      setStatistics(statisticsData);
    } catch (error: any) {
      setToast({
        type: "error",
        message: error?.message || "Failed to load SDLC data",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filteredClusters = clusters.filter((c) => {
    const matchesSearch =
      searchTerm === "" ||
      c.cluster_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.cluster_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.business_domain?.toLowerCase().includes(searchTerm.toLowerCase());
    return (
      matchesSearch &&
      (statusFilter === "ALL" || c.cluster_status === statusFilter) &&
      (typeFilter === "ALL" || c.cluster_type === typeFilter)
    );
  });

  const totalRecords = filteredClusters.length;
  const paginatedClusters = filteredClusters.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  const handleViewCluster = (cluster: InventoryCluster) => {
    setSelectedCluster(cluster);
    setClusterDetailsModalOpen(true);
  };

  return (
    <div
      className={`netbody-card flex flex-col h-full ${compact ? "" : "min-h-[500px]"}`}
    >
      {/* Toolbar — Show LEFT · filters + search + Create RIGHT */}
      <div className="netbody-header">
        {/* Quick-access chips toggle */}
        <div className="relative">
          <button
            onClick={() => setShowChips((v) => !v)}
            title="Quick access"
            data-testid="BTN_sdlc_chips_toggle"
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-xs font-medium transition-colors ${
              showChips
                ? "bg-purple-600 border-purple-600 text-white"
                : "bg-white border-gray-300 text-gray-600 hover:border-purple-400 hover:text-purple-600"
            }`}
          >
            <SlidersHorizontal size={13} />
          </button>

          {showChips && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowChips(false)}
              />
              <div
                className="absolute left-0 top-full mt-2 z-20 bg-white border border-gray-200 rounded-lg shadow-lg p-3 flex flex-col gap-2 min-w-[160px]"
                style={{ animation: "fadeSlideDown 0.18s ease" }}
              >
                {[
                  {
                    label: "Categories",
                    icon: "📂",
                    count: statistics?.components_by_category
                      ? Object.keys(statistics.components_by_category).length
                      : 0,
                    onClick: () => {
                      setCategoriesModalOpen(true);
                      setShowChips(false);
                    },
                  },
                  {
                    label: "Types",
                    icon: "🏷️",
                    count: "12+",
                    onClick: () => {
                      setTypesModalOpen(true);
                      setShowChips(false);
                    },
                  },
                  {
                    label: "Statuses",
                    icon: "📊",
                    count: statistics?.components_by_status
                      ? Object.keys(statistics.components_by_status).length
                      : 0,
                    onClick: () => {
                      setStatusesModalOpen(true);
                      setShowChips(false);
                    },
                  },
                  {
                    label: "Inventory",
                    icon: "📋",
                    count: statistics?.total_components || 0,
                    onClick: () => {
                      setInventoryModalOpen(true);
                      setShowChips(false);
                    },
                  },
                  {
                    label: "Clusters",
                    icon: "🗂️",
                    count: statistics?.total_clusters || 0,
                    onClick: () => {
                      setClustersModalOpen(true);
                      setShowChips(false);
                    },
                  },
                ].map((item) => (
                  <button
                    key={item.label}
                    onClick={item.onClick}
                    data-testid={`BTN_sdlc_chip_${item.label.toLowerCase()}`}
                    className="flex items-center gap-2 px-3 py-2 rounded text-xs font-medium text-gray-700 bg-gray-50 hover:bg-purple-50 hover:text-purple-700 border border-gray-200 hover:border-purple-300 transition-colors text-left"
                  >
                    <span>{item.icon}</span>
                    <span className="flex-1">{item.label}</span>
                    <span className="bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-full text-[10px]">
                      {item.count}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.SearchSelect
            value={statusFilter}
            onChange={(v) => setStatusFilter(String(v ?? 'ALL'))}
            className="filter-status"
            data-testid="SEL_sdlc_status"
            noSearchIcon
            options={[
              { value: 'ALL', label: 'All Statuses' },
              { value: 'ACTIVE', label: 'Active' },
              { value: 'IN_PROGRESS', label: 'In Progress' },
              { value: 'PLANNED', label: 'Planned' },
              { value: 'DEPRECATED', label: 'Deprecated' },
              { value: 'ARCHIVED', label: 'Archived' },
            ]}
          />
          <FormControl.Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            data-testid="SEL_sdlc_type"
          >
            <option value="ALL">All Types</option>
            <option value="SYSTEM">System</option>
            <option value="MODULE">Module</option>
            <option value="FEATURE">Feature</option>
            <option value="COMPONENT">Component</option>
            <option value="WORKFLOW">Workflow</option>
          </FormControl.Select>
          <FormControl.Input
            type="text"
            placeholder="Search clusters…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
            data-testid="INP_sdlc_search"
          />
          <Button
            variant="fill"
            icon={Plus}
            onClick={() => setClustersModalOpen(true)}
            data-testid="BTN_sdlc_create"
          >
            Create
          </Button>
        </div>
      </div>

      <div className="netbody-content">
        {/* Stats bar */}
        {statistics && (
          <div style={{ display: "flex", gap: "12px", marginBottom: "8px" }}>
            {[
              { label: "Components", val: statistics.total_components },
              { label: "Clusters", val: statistics.total_clusters },
              { label: "Critical", val: statistics.critical_components },
              {
                label: "Test Coverage",
                val: `${Math.round(statistics.avg_test_coverage)}%`,
              },
            ].map((s) => (
              <div
                key={s.label}
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  padding: "4px 12px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "#1e293b",
                  }}
                >
                  {s.val}
                </div>
                <div
                  style={{
                    fontSize: "10px",
                    color: "#94a3b8",
                    textTransform: "uppercase",
                  }}
                >
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="scroll-container flex-1 overflow-hidden">
          <div className="table-list">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Code</th>
                    <th className="table-list-th">Name</th>
                    <th className="table-list-th">Type</th>
                    <th className="table-list-th">Domain</th>
                    <th className="table-list-th table-list-cell--status">Status</th>
                    <th className="table-list-th">Priority</th>
                    <th className="table-list-th">Completion</th>
                    <th className="table-list-th table-list-th--right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody aria-busy={loading} aria-live="polite">
                  {loading && <Skeleton.TableRows cols={['3xs', 'md', 'sm', 'pill', { width: 'icon', align: 'right', count: 2 }]} />}
                  {!loading && filteredClusters.length === 0 && (
                    <tr>
                      <td colSpan={9} className="table-list-empty-cell">
                        No clusters found. Click "Create" to add one.
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    paginatedClusters.map((cluster, idx) => (
                      <tr
                        key={cluster.id}
                        className="table-list-row"
                        onClick={() => handleViewCluster(cluster)}
                        data-testid={`BTN_sdlc_row_${cluster.id}_open`}
                      >
                        <td className="table-list-cell table-list-cell--sno">
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className="table-list-cell">
                          <span className="text-xs text-slate-500">
                            {cluster.cluster_code}
                          </span>
                        </td>
                        <td className="table-list-cell">
                          <span className="table-list-cell-title">
                            {cluster.cluster_name}
                          </span>
                          {cluster.cluster_description && (
                            <span className="table-list-cell-sub block">
                              {cluster.cluster_description}
                            </span>
                          )}
                        </td>
                        <td className="table-list-cell">
                          <span className="status-badge xs info">
                            {cluster.cluster_type}
                          </span>
                        </td>
                        <td className="table-list-cell">
                          {cluster.business_domain || "—"}
                        </td>
                        <td className="table-list-cell">
                          <Badge variant={statusVariant(cluster.cluster_status)} size="sm">
                            {cluster.cluster_status.replace("_", " ")}
                          </Badge>
                        </td>
                        <td className="table-list-cell">
                          <span className={severityBadgeCss(cluster.priority_level)}>
                            {cluster.priority_level}
                          </span>
                        </td>
                        <td className="table-list-cell">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              minWidth: 80,
                            }}
                          >
                            <div
                              style={{
                                flex: 1,
                                height: 6,
                                background: "#e2e8f0",
                                borderRadius: 3,
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  height: "100%",
                                  width: `${cluster.completion_percentage}%`,
                                  background: "#3b82f6",
                                  borderRadius: 3,
                                }}
                              />
                            </div>
                            <span
                              style={{
                                fontSize: 11,
                                color: "#64748b",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {cluster.completion_percentage}%
                            </span>
                          </div>
                        </td>
                        <td className="table-list-cell table-list-cell--actions">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewCluster(cluster);
                            }}
                            className="table-list-action table-list-action--edit"
                            title="View details"
                            data-testid={`BTN_sdlc_row_${cluster.id}_view`}
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setClustersModalOpen(true);
                              setSelectedCluster(cluster);
                            }}
                            className="table-list-action table-list-action--edit"
                            title="Edit"
                            data-testid={`BTN_sdlc_row_${cluster.id}_edit`}
                          >
                            <Edit2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              recordLabel="clusters"
            />
          </div>
        </div>
      </div>

      <InventoryCategoriesModal
        isOpen={categoriesModalOpen}
        onClose={() => setCategoriesModalOpen(false)}
      />
      <InventoryTypesModal
        isOpen={typesModalOpen}
        onClose={() => setTypesModalOpen(false)}
      />
      <InventoryStatusesModal
        isOpen={statusesModalOpen}
        onClose={() => setStatusesModalOpen(false)}
      />
      <InventoryItemsModal
        isOpen={inventoryModalOpen}
        onClose={() => setInventoryModalOpen(false)}
      />
      <InventoryClustersModal
        isOpen={clustersModalOpen}
        onClose={() => {
          setClustersModalOpen(false);
          setSelectedCluster(null);
          refresh();
        }}
      />
      <ClusterDetailsModal
        isOpen={clusterDetailsModalOpen}
        onClose={() => {
          setClusterDetailsModalOpen(false);
          setSelectedCluster(null);
        }}
        cluster={selectedCluster}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default SDLCManagement;
